// Foundations
import {
  AfterContentChecked,
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CarouselComponent } from 'angular-responsive-carousel';

// Services
import { Booking } from '../../../services/graphql.service';

@Component({
  selector: 'app-landing-upcoming',
  templateUrl: './landing-upcoming.component.html',
  styleUrls: ['./landing-upcoming.component.scss'],
})
export class LandingUpcomingComponent
  implements OnInit, AfterContentInit, AfterContentChecked
{
  MOBILE_SCREEN_WIDTH_THRESHOLD = 576;
  CAROUSEL_WIDTH_DEFAULT = 398;
  CAROUSEL_WIDTH_IN_MOBILE = 308;
  // The "offset" is used to host the "arrows"
  CAROUSEL_CONTAINER_WIDTH_OFFSET = 110;

  @ViewChild('bookingCarousel', { static: false })
  bookingCarouselComponent: CarouselComponent;
  @Input() bookingList: Booking[];

  isInMobile = false;
  carouselHeight = 0;
  carouseWidthAdjusted = false;
  allowScrolling = false;

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    // Decide the "mobile" mode
    if (window.innerWidth <= this.MOBILE_SCREEN_WIDTH_THRESHOLD) {
      this.isInMobile = true;
    }
  }

  ngAfterContentInit() {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterContentChecked() {
    this._adjustElementState();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  @HostBinding('window:resize') onResize(event) {
    // Decide if need to change the "mode"
    if (event.target.innerWidth <= this.MOBILE_SCREEN_WIDTH_THRESHOLD) {
      // Entering the "mobile" mode
      if (!this.isInMobile) {
        this.isInMobile = true;
        this._adjustElementState();
      }
    } else {
      if (this.isInMobile) {
        this.isInMobile = false;
        this._adjustElementState();
      }
    }
  }

  private _adjustElementState() {
    //
    this.carouselHeight = this._decideCarouselMaxHeight();
    this.allowScrolling = this._decideCarouselScrolling();
    /*
      Here, the adjustment of `height` (by `carouselHeight`) will cause the
        patches to `width` being overwritten.
      So that, add a timer to delay the patching.
     */
    setTimeout(() => {
      this._decideCarouselWidth();
    }, 500);
  }

  /**
   * Determine the actual "visible width" for the "Carousel" area.
   *
   * NOTES:
   * - The current `angular-responsive-carousel` plugin could not take the customize CSS stylings (like `width`) for `carousel-container`.
   *  - In other words, if defining it, we need to "Manually" adjust the "visible width".
   *
   * TODO - Update this logic once updating the plugin if needed - `angular-responsive-carousel`.
   *
   */
  private _decideCarouselWidth() {
    // Only do the adjust ONCE.
    if (this.bookingCarouselComponent && !this.carouseWidthAdjusted) {
      // Get the "actual width"
      const nativeElementWidth =
        this.bookingCarouselComponent.savedCarouselWidth;

      // Assign the adjusted value to `visibleWidth`
      this.bookingCarouselComponent.carouselProperties.visibleWidth =
        nativeElementWidth - this.CAROUSEL_CONTAINER_WIDTH_OFFSET;
      // Call `resize()` to apply the new value to all its child objects.
      this.bookingCarouselComponent.resize();

      //
      this.carouseWidthAdjusted = true;
    }
  }

  /**
   * Use the "height" from the "highest" tile.
   */
  private _decideCarouselMaxHeight() {
    // Get all tiles
    const childrenTiles = (
      this.elementRef.nativeElement as HTMLElement
    ).getElementsByClassName('landing-bookings-tile-container');

    if (!childrenTiles || childrenTiles.length === 0) {
      return 0;
    }

    // Reset the height
    Array.from(childrenTiles).forEach(
      (ele: HTMLElement) => (ele.style.height = 'initial')
    );

    // Collect the height
    const itemHeights = Array.from(childrenTiles).map(
      (ele: HTMLElement) => ele.getBoundingClientRect().height
    );
    const maxHeight = itemHeights.reduce(
      (heightA, heightB) => (heightA > heightB ? heightA : heightB),
      0
    );

    // Add an "offset" to the final "height"
    return maxHeight + 16;
  }

  private _decideCarouselScrolling() {
    if (Array.isArray(this.bookingList)) {
      const tileWidth = this.isInMobile
        ? this.CAROUSEL_WIDTH_IN_MOBILE
        : this.CAROUSEL_WIDTH_DEFAULT;
      return this.bookingList.length * tileWidth > window.innerWidth;
    }

    return false;
  }
}
